import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { IApiErrorPlain, IRecord } from '@platform/core/types';
import { useUploadFileMutation } from '@platform/state/api';
import { useRecordRefresh } from '../useRecordRefresh';
import { asPlainError } from './asPlainError';
import { MAX_FILE_MB, splitBySize } from './fileLimits';

/**
 * Завантаження файлів запису (прототип addFiles): кілька файлів за раз, по черзі; завеликі (понад 25 МБ) не надсилаються й названі в
 * повідомленні; після завантаження оновлюються лічильники запису. Спільне для вкладки «Файли» і «+» пігулки файлів на панелі дій.
 */
export function useFileUploads(record: IRecord) {
  const { t } = useTranslation();
  const [uploadFile] = useUploadFileMutation();
  const refresh = useRecordRefresh(record);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<IApiErrorPlain | null>(null);
  const upload = useCallback(async (files: File[]) => {
    const { ok, tooBig } = splitBySize(files);
    setError(tooBig.length ? { status: 413, code: 'file_too_large', errors: {}, message: tooBig.map((f) => t('recordPage.files.tooBig', { name: f.name, max: MAX_FILE_MB })).join(' ') } : null);
    if (!ok.length) return;
    setBusy(true);
    try {
      for (const file of ok) await uploadFile({ recordId: record.id, file }).unwrap();
    } catch (e) {
      setError(asPlainError(e));
    } finally {
      setBusy(false);
      refresh();
    }
  }, [uploadFile, record.id, refresh, t]);
  return { upload, busy, error };
}
